/**
 * Pure Focus helpers — plannability, day layout, budget rows, start defaults
 * and the display vocabulary. No React, no fetches.
 */
import type {
  ChangeKind,
  FocusAudience,
  FocusBlock,
  FocusGoalStatus,
  OutlookEvent,
  PendingChanges,
  TimerPhase,
  TimerState,
  WeekGoal,
} from "../api/focus";
import type { PillTone } from "../components/Pill";
import { berlinDate } from "./focusTime";

export type FocusView = "today" | "week" | "goals";

export const FOCUS_VIEWS: { value: FocusView; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "week", label: "Week" },
  { value: "goals", label: "Goals" },
];

export const AUDIENCES: { value: FocusAudience; label: string; hint: string }[] = [
  { value: "me", label: "Me", hint: "Only you work on it" },
  { value: "ai", label: "AI team", hint: "The AI team works on it; you plan no time for it" },
  { value: "all", label: "Both", hint: "You and the AI team" },
];

export const PHASE_LABEL: Record<TimerPhase, string> = {
  idle: "Idle",
  focus: "Focus",
  short_break: "Break",
  long_break: "Long break",
};

export const STATUS_TONE: Record<FocusGoalStatus, PillTone> = {
  active: "ok",
  paused: "muted",
  achieved: "ok",
  retired: "muted",
};

export const CHANGE_KIND: Record<ChangeKind, { label: string; tone: PillTone }> = {
  goal_created: { label: "new goal", tone: "ok" },
  goal_updated: { label: "goal", tone: "muted" },
  goals_reranked: { label: "priorities", tone: "signal" },
  block_added: { label: "block +", tone: "ok" },
  block_removed: { label: "block −", tone: "muted" },
  block_restored: { label: "block restored", tone: "ok" },
  block_moved: { label: "block moved", tone: "signal" },
  block_updated: { label: "block", tone: "muted" },
};

/** Active goals the owner works on themself (audience me/all) take focus time. */
export function isPlannable(goal: {
  plannable?: boolean;
  status: string;
  audience: string;
}): boolean {
  if (typeof goal.plannable === "boolean") return goal.plannable;
  return goal.status === "active" && (goal.audience === "me" || goal.audience === "all");
}

export const isBreak = (phase: TimerPhase) => phase === "short_break" || phase === "long_break";

export interface DayItem {
  id: string;
  startMin: number;
  endMin: number;
}

/**
 * Side-by-side columns for overlapping items of one day: each cluster of
 * transitively overlapping items shares a column count, and every item takes
 * the first column free at its start.
 */
export function layoutDayItems(items: DayItem[]): Map<string, { col: number; cols: number }> {
  const sorted = [...items].sort((a, b) => a.startMin - b.startMin || b.endMin - a.endMin);
  const result = new Map<string, { col: number; cols: number }>();
  let cluster: { id: string; col: number }[] = [];
  let columnEnds: number[] = [];
  let clusterEnd = -Infinity;
  const flush = () => {
    for (const entry of cluster) result.set(entry.id, { col: entry.col, cols: columnEnds.length });
    cluster = [];
    columnEnds = [];
  };
  for (const item of sorted) {
    if (item.startMin >= clusterEnd) {
      flush();
      clusterEnd = -Infinity;
    }
    let col = columnEnds.findIndex((end) => end <= item.startMin);
    if (col === -1) {
      col = columnEnds.length;
      columnEnds.push(item.endMin);
    } else {
      columnEnds[col] = item.endMin;
    }
    cluster.push({ id: item.id, col });
    clusterEnd = Math.max(clusterEnd, item.endMin);
  }
  flush();
  return result;
}

export interface BudgetRow {
  id: string;
  rank: number | null;
  title: string;
  plannable: boolean;
  budget: number | null;
  planned: number;
  actual: number;
  /** Planned / actual as a share of the budget (0–1, may exceed 1). */
  plannedShare: number | null;
  actualShare: number | null;
  overPlanned: boolean;
}

export function budgetRows(goals: WeekGoal[]): BudgetRow[] {
  return [...goals]
    .sort((a, b) => (a.rank ?? Number.MAX_SAFE_INTEGER) - (b.rank ?? Number.MAX_SAFE_INTEGER))
    .map((g) => {
      const budget = g.budget_minutes;
      return {
        id: g.id,
        rank: g.rank,
        title: g.title,
        plannable: isPlannable(g),
        budget,
        planned: g.planned_minutes,
        actual: g.actual_minutes,
        plannedShare: budget ? g.planned_minutes / budget : null,
        actualShare: budget ? g.actual_minutes / budget : null,
        overPlanned: budget != null && g.planned_minutes > budget,
      };
    });
}

/** Which goal the Start picker preselects: the server's suggestion, then the top-ranked plannable goal. */
export function defaultStartGoal(
  timer: Pick<TimerState, "suggested_goal_id"> | undefined,
  goals: { id: string; rank: number | null; plannable: boolean }[],
): string | null {
  const plannable = goals.filter((g) => g.plannable);
  if (timer?.suggested_goal_id && plannable.some((g) => g.id === timer.suggested_goal_id)) {
    return timer.suggested_goal_id;
  }
  const top = [...plannable].sort(
    (a, b) => (a.rank ?? Number.MAX_SAFE_INTEGER) - (b.rank ?? Number.MAX_SAFE_INTEGER),
  )[0];
  return top?.id ?? null;
}

/** Outlook events that make a meeting conflict (timed, busy or out of office). */
export function isBusyEvent(event: OutlookEvent): boolean {
  return !event.all_day && (event.show_as === "busy" || event.show_as === "oof");
}

/** An all-day event covers [its date, its end date) — the end is the next midnight. */
export function allDayCovers(event: OutlookEvent, date: string): boolean {
  const start = event.date ?? (event.starts_at ? berlinDate(Date.parse(event.starts_at)) : null);
  if (!start) return false;
  const end = event.ends_at ? berlinDate(Date.parse(event.ends_at)) : null;
  if (!end || end <= start) return date === start;
  return start <= date && date < end;
}

/** Does a block overlap a busy Outlook meeting? */
export function overlapsBusy(block: FocusBlock, events: OutlookEvent[]): boolean {
  const start = Date.parse(block.starts_at);
  const end = Date.parse(block.ends_at);
  return events.some((e) => {
    if (!isBusyEvent(e) || !e.starts_at || !e.ends_at) return false;
    return start < Date.parse(e.ends_at) && end > Date.parse(e.starts_at);
  });
}

export type BlockTiming = "future" | "running" | "past";

/** Past blocks only take a note; a running one may change its end. */
export function blockTiming(block: FocusBlock, nowMs: number): BlockTiming {
  if (Date.parse(block.ends_at) <= nowMs) return "past";
  if (Date.parse(block.starts_at) <= nowMs) return "running";
  return "future";
}

/** "Linda" — the button and toast name for the commit target. */
export function chiefOfStaffName(pending: PendingChanges | undefined): string {
  return pending?.chief_of_staff?.name ?? pending?.chief_of_staff?.slug ?? "chief of staff";
}
